import { ViewResolver } from '@Components/index';
import { Flex } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';
import { PurchaseFormResponse, PurchaseItemResponse } from '@Types/api/form';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { NotificationContext } from '@Context/NotificationContext';
import {
  currentOverviewItems,
  currentSelectedOverviewItems,
  createdTender,
} from '../../store/selectors/purchaseRequestSelector';
import { purchaseRequestActions } from '../../store/purchaseRequestSlice';
import {
  useCreateTenderMutation,
  useGetPurchaseItemsMutation,
  useGetSearchListPurchaseRequestsMutation,
} from '../../store/api/purchaseRequestApiSlice';
import CreateTenderDesktop from './desktop/CreateTenderDesktop';
import CreateTenderMobile from './mobile/CreateTenderMobile';

const PurchaseRequestCreateForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tenderId = useAppSelector(createdTender);
  const { successNotification, errorNotification } =
    useContext(NotificationContext);
  const [getPurchaseItems] = useGetPurchaseItemsMutation();
  const [createTender] = useCreateTenderMutation();
  const [purchaseItems, setPurchaseItems] = useState<
    Record<number, PurchaseItemResponse[]>
  >({});
  const [listRequests, setListRequests] = useState<
    RequestListSearchResponse<PurchaseFormResponse>
  >({} as RequestListSearchResponse<PurchaseFormResponse>);
  const [requestsDataList, setRequestsDataList] = useState<
    Array<PurchaseFormResponse>
  >([]);
  const [currentPage, setCurrentPage] = useState(listRequests?.page || 0);
  const [getListPurchaseRequests] = useGetSearchListPurchaseRequestsMutation();
  const overviewItems = useAppSelector(currentOverviewItems);
  const selectedOverviewItems = useAppSelector(currentSelectedOverviewItems);

  const onGetPurchaseItems = (requestId: number): void => {
    getPurchaseItems(requestId)
      .unwrap()
      .then((response) => {
        setPurchaseItems((prevItems) => ({
          ...prevItems,
          [requestId]: response,
        }));
      });
  };

  const loadMore = (): void => {
    if (loading) {
      return;
    }
    setLoading(true);
    getListPurchaseRequests({ page: currentPage })
      .unwrap()
      .then((response) => {
        setCurrentPage(currentPage + 1);
        setListRequests(response);
        if (response?.content) {
          setRequestsDataList([...requestsDataList, ...response.content]);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadMore();
  }, []);

  const handleCreateTender = useCallback(() => {
    if (selectedOverviewItems && tenderId === 0) {
      const itemsIDs = selectedOverviewItems.map((item) => item.reqItemId);

      createTender({
        reqItemIds: itemsIDs,
      })
        .unwrap()
        .then((data) => {
          successNotification(`Tender No. ${data.id} has been created!`);
          dispatch(purchaseRequestActions.setCreatedTenderId(data.id));
          dispatch(purchaseRequestActions.removeAllOverviewItem());
        })
        .catch((error) => {
          errorNotification(error.data);
        });
    }
  }, [selectedOverviewItems]);

  const handleReviewTender = useCallback(() => {
    if (selectedOverviewItems && tenderId === 0) {
      const itemsIDs = selectedOverviewItems.map((item) => item.reqItemId);

      createTender({
        reqItemIds: itemsIDs,
      })
        .unwrap()
        .then((data) => {
          successNotification(`Tender No. ${data.id} has been created!`);
          dispatch(purchaseRequestActions.setCreatedTenderId(data.id));
          dispatch(purchaseRequestActions.removeAllOverviewItem());
          navigate(
            `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}/confirm`
          );
        })
        .catch((error) => {
          errorNotification(error.data);
        });
    } else if (tenderId !== 0) {
      navigate(
        `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}/confirm`
      );
    }
  }, [selectedOverviewItems, tenderId]);

  return (
    <Flex gap={10} vertical>
      <ViewResolver
        desktop={
          <CreateTenderDesktop
            listRequests={listRequests}
            requestsDataList={requestsDataList}
            loadMore={loadMore}
            purchaseItems={purchaseItems}
            onGetPurchaseItems={onGetPurchaseItems}
            overviewItems={overviewItems}
            selectedOverviewItems={selectedOverviewItems}
            handleCreateTender={handleCreateTender}
            handleReviewTender={handleReviewTender}
          />
        }
        mobile={
          <CreateTenderMobile
            listRequests={listRequests}
            requestsDataList={requestsDataList}
            purchaseItems={purchaseItems}
            loadMore={loadMore}
            onGetPurchaseItems={onGetPurchaseItems}
            overviewItems={overviewItems}
            selectedOverviewItems={selectedOverviewItems}
            handleCreateTender={handleCreateTender}
            handleReviewTender={handleReviewTender}
          />
        }
      />
    </Flex>
  );
};

export default PurchaseRequestCreateForm;
